import React, { useState } from 'react';

import clsx from 'clsx';

import { Checkbox } from '@/components/ui/checkbox';

export type ABTestStatus = 'running' | 'stopped' | 'draft';

const STATUS_STYLES: Record<ABTestStatus, { bg: string; text: string; label: string }> = {
  running: { bg: 'bg-green-100', text: 'text-green-500', label: 'Running' },
  stopped: { bg: 'bg-neutral-100', text: 'text-neutral-500', label: 'Stopped' },
  draft: { bg: 'bg-neutral-100', text: 'text-neutral-400', label: 'Draft' },
};

interface ABTestItemProps {
  id: string;
  name: string;
  status: ABTestStatus;
  selected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  className?: string;
}

export const ABTestItem: React.FC<ABTestItemProps> = ({
  id,
  name,
  status,
  selected = false,
  onSelect,
  className,
}) => {
  const [hovered, setHovered] = useState(false);
  const style = STATUS_STYLES[status];
  const showCheckbox = hovered || selected;

  return (
    <div
      className={clsx(
        'flex h-12 cursor-pointer items-center gap-2 rounded-lg p-2 transition-all duration-200 ease-out',
        hovered || selected ? 'bg-indigo-100' : 'outline-1 -outline-offset-1 outline-neutral-100',
        className,
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect?.(id, !selected)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(id, !selected);
        }
      }}
      role="button"
      aria-pressed={selected}
      tabIndex={0}
    >
      <div
        className={clsx(
          'grid transition-[grid-template-columns,opacity] duration-200 ease-out',
          showCheckbox ? 'grid-cols-[1rem] opacity-100' : 'grid-cols-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) => onSelect?.(id, !!checked)}
            onClick={(e) => e.stopPropagation()}
            tabIndex={showCheckbox ? 0 : -1}
            className="shrink-0"
          />
        </div>
      </div>
      <span className="text-xs leading-4 font-medium text-neutral-500">{name}</span>
      <div
        className={clsx(
          'flex h-5 items-center justify-center rounded-full px-2 py-0.5 outline-1 -outline-offset-1 outline-neutral-100',
          style.bg,
        )}
      >
        <span className={clsx('text-center text-[10px] font-semibold', style.text)}>
          {style.label}
        </span>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────
   ABTestList — manages selection state for a list of items
   ────────────────────────────────────────────────────────── */
export interface ABTestListItem {
  id: string;
  name: string;
  status: ABTestStatus;
}

interface ABTestListProps {
  tests: ABTestListItem[];
  onSelect?: (id: string) => void;
  className?: string;
}

export const ABTestList: React.FC<ABTestListProps> = ({ tests, onSelect, className }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
    onSelect?.(id);
  };

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {tests.map((test) => (
        <ABTestItem
          key={test.id}
          id={test.id}
          name={test.name}
          status={test.status}
          selected={selectedIds.has(test.id)}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
};
